// ============================================
// Journey Configuration
// ============================================
//
// Centralizes all journey step names, touchpoint
// labels, and triggers so the engine can serve
// different buildings (HemoLogix, Medek, Medicaring)
// by swapping this config — no code changes needed.
// ============================================

import type { Touchpoint } from '../types/patientJourney';

// --- Journey Step Keys (single source of truth) ---
// All files derive JourneyStep type from this array.
// To add/rename steps for a different building, change this config.

export const JOURNEY_STEP_KEYS = [
  'triage',
  'upload',
  'processing',
  'chat',
  'protocol',
  'checkout',
  'complete',
] as const;

export type JourneyStepKey = typeof JOURNEY_STEP_KEYS[number];

// --- Journey Step Labels (JourneyService / MyResults) ---

export type JourneyStepLabels = Record<JourneyStepKey, string>;

// --- Touchpoint Definition (JourneyEngine / JourneyTimeline) ---

export type TouchpointType = Touchpoint['type'];

export interface TouchpointDefinition {
  touchpointId: string;
  type: TouchpointType;
  order: number;
  label: string;
  trigger: string;
}

// --- Screen-to-Step Mapping (JourneyService) ---
// Maps AppScreen values to journey step keys.
// Different buildings may have different screen flows.

export type ScreenToStepMap = Record<string, JourneyStepKey>;

// --- Full Journey Config ---

export interface JourneyConfig {
  stepLabels: JourneyStepLabels;
  screenToStepMap: ScreenToStepMap;
  touchpoints: TouchpointDefinition[];
}

// --- Default HemoLogix Configuration ---

export const DEFAULT_JOURNEY_CONFIG: JourneyConfig = {
  stepLabels: {
    triage: 'Intake',
    upload: 'Lab Upload',
    processing: 'Processing',
    chat: 'Consultation',
    protocol: 'Protocol',
    checkout: 'Checkout',
    complete: 'Complete',
  },
  screenToStepMap: {
    triage: 'triage',
    processing: 'processing',
    chat: 'chat',
    protocol: 'protocol',
    checkout_review: 'checkout',
    report: 'chat',       // report is viewed within chat flow
    landing: 'triage',    // landing defaults to triage
  },
  touchpoints: [
    {
      touchpointId: 'tp_first_contact',
      type: 'first_contact',
      order: 1,
      label: 'First Contact',
      trigger: 'Labs uploaded, AI Nurse conversation initiated',
    },
    {
      touchpointId: 'tp_checkout',
      type: 'checkout',
      order: 2,
      label: 'Checkout',
      trigger: 'User initiates purchase of Gate 1 protocol',
    },
    {
      touchpointId: 'tp_mid_protocol',
      type: 'mid_protocol',
      order: 3,
      label: 'Mid-Protocol Check-In',
      trigger: '30-45 days on protocol, automated check-in',
    },
    {
      touchpointId: 'tp_retest',
      type: 'retest',
      order: 4,
      label: '90-Day Retest',
      trigger: 'New labs uploaded for comparison to baseline',
    },
    {
      touchpointId: 'tp_med_change',
      type: 'med_change',
      order: 5,
      label: 'Medication Change',
      trigger: 'Patient reports new medication during protocol',
    },
    {
      touchpointId: 'tp_gate2',
      type: 'gate2_transition',
      order: 6,
      label: 'Gate 2 Transition',
      trigger: 'Labs warrant escalation to telehealth/Rx pathway',
    },
    {
      touchpointId: 'tp_success',
      type: 'protocol_success',
      order: 7,
      label: 'Protocol Success',
      trigger: 'Two consecutive draws show biomarkers in functional range',
    },
    {
      touchpointId: 'tp_cancellation',
      type: 'cancellation',
      order: 8,
      label: 'Cancellation',
      trigger: 'User initiates subscription cancellation',
    },
    {
      touchpointId: 'tp_reengagement',
      type: 're_engagement',
      order: 9,
      label: 'Re-engagement',
      trigger: 'User returns after 90+ day gap',
    },
  ],
};

// --- Active Config (swap for different buildings) ---

let activeConfig: JourneyConfig = DEFAULT_JOURNEY_CONFIG;

export function getJourneyConfig(): JourneyConfig {
  return activeConfig;
}

export function setJourneyConfig(config: JourneyConfig): void {
  activeConfig = config;
}

/** Get display label for a JourneyStep key. Falls back to the key itself. */
export function getStepLabel(step: string): string {
  return activeConfig.stepLabels[step as keyof JourneyStepLabels] ?? step;
}

/** Map a screen name to a journey step key. Falls back to 'triage'. */
export function getScreenToStep(screen: string): JourneyStepKey {
  return activeConfig.screenToStepMap[screen] ?? 'triage';
}

/** Get touchpoint definition by type. */
export function getTouchpointDef(type: TouchpointType): TouchpointDefinition | undefined {
  return activeConfig.touchpoints.find((t) => t.type === type);
}
